import {Table, Typography, nodeColumn, TableActionsCell} from '@sollapay/ui/components';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {DateCell} from '@/components';
import {
  useTrustAccountPermissions,
  useTrustTypeStrategy
} from '@/features/trust-accounts/providers';

import {canDeleteBuyerRow, formatPurchaseAssetNames, formatBuyerDisplayName} from '../utils';
import {BuyerActionButton} from './BuyerActionButton';
import {BuyerAvatarStack} from './BuyerAvatarStack';
import {BuyerMoreMenu} from './BuyerMoreMenu';
import {PaymentInstructionStatusBadge} from './PaymentInstructionStatusBadge';
import {
  createAmountToPayColumn,
  createAssetColumn,
  createDepositPaidColumn,
  createPaymentStatusColumn,
  createPurchasePriceColumn,
  createRegulatoryReportsColumn
} from './PurchaseColumns';

import type {BuyerTableRow} from '../types/buyers.types';
import type {BuyerDTO, BuyerPurchaseDTO} from '@sollapay/types';
import type {ListOnSortingChange, ListSortingState} from '@sollapay/ui/components';
import type {FC} from 'react';

export interface BuyerTableProps {
  rows: BuyerTableRow[];
  sorting: ListSortingState;
  onSortingChange: ListOnSortingChange;
  onRequestPayment: (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => void;
  onViewBuyer: (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => void;
  onEditBuyer: (buyers: BuyerDTO[]) => void;
  onDeleteBuyer: (buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => void;
  trustAccountId: string;
  showsCoBuyersInOneRow: boolean;
}

export const BuyerTable: FC<BuyerTableProps> = ({
  rows,
  sorting,
  onSortingChange,
  onRequestPayment,
  onViewBuyer,
  onEditBuyer,
  onDeleteBuyer,
  trustAccountId,
  showsCoBuyersInOneRow
}) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const {canUpdateBuyer, canDeleteBuyer, canCollectPayments} = useTrustAccountPermissions();
  const strategy = useTrustTypeStrategy();

  const columns = useMemo(() => {
    const fullNameColumn = nodeColumn<BuyerTableRow>({
      id: 'fullName',
      header: t('view.buyers.list.columns.buyerName'),
      maxWidth: 200,
      accessorFn: row => formatBuyerDisplayName(row.buyers[0].fullName),
      cell: row => {
        const displayName = formatBuyerDisplayName(row.buyers[0].fullName);
        return (
          <div className="flex flex-row items-center gap-3">
            <BuyerAvatarStack
              buyers={row.buyers}
              assetLabel={formatPurchaseAssetNames(row.purchase.assets, t)}
              onViewBuyer={buyer => onViewBuyer(buyer, row.purchase)}
            />
            {row.buyers.length === 1 && (
              <Typography
                size="sm"
                weight="semibold"
                className="decoration-1 group-hover/row:text-brand-accent group-hover/row:underline"
              >
                {displayName}
              </Typography>
            )}
          </div>
        );
      }
    });

    const latestPaymentStatusColumn = nodeColumn<BuyerTableRow>({
      id: 'latestPaymentStatus',
      header: t('view.buyers.list.columns.latestPaymentStatus'),
      accessorFn: row => row.latestInstruction?.status ?? '',
      cell: row =>
        row.latestInstruction ? (
          <PaymentInstructionStatusBadge instruction={row.latestInstruction} />
        ) : (
          <Typography size="sm" color="tertiary">
            —
          </Typography>
        )
    });

    const createdAtColumn = nodeColumn<BuyerTableRow>({
      id: 'createdAt',
      header: t('view.buyers.list.columns.createdAt'),
      accessorFn: row => String(row.buyers[0].createdAt),
      cell: row => <DateCell date={row.buyers[0].createdAt} />
    });

    const actionsColumn = nodeColumn<BuyerTableRow>({
      id: 'actions',
      header: '',
      width: 'min',
      enableSorting: false,
      cell: row => {
        const canDeleteThisRow = canDeleteBuyerRow(row.buyers, canDeleteBuyer);

        return (
          <TableActionsCell>
            <BuyerActionButton
              showRequestPayment={canCollectPayments && row.canRequestPayment}
              onRequestPayment={() => onRequestPayment(row.buyers[0], row.purchase)}
              onViewBuyer={() => onViewBuyer(row.buyers[0], row.purchase)}
            />
            <div className="flex size-9 shrink-0 items-center justify-center">
              <BuyerMoreMenu
                actions={canDeleteThisRow ? ['edit', 'view', 'delete'] : ['edit', 'view']}
                onView={() => onViewBuyer(row.buyers[0], row.purchase)}
                onEdit={canUpdateBuyer ? () => onEditBuyer(row.buyers) : undefined}
                onDelete={
                  canDeleteThisRow ? () => onDeleteBuyer(row.buyers[0], row.purchase) : undefined
                }
              />
            </div>
          </TableActionsCell>
        );
      }
    });

    // General sale renders the asset/price once in AssetTrustSummaryCard instead — status and
    // deposit paid stay here since they're per-buyer, not shared across the whole purchase.
    if (!showsCoBuyersInOneRow) {
      return [
        fullNameColumn,
        createPaymentStatusColumn<BuyerTableRow>(t, true),
        createDepositPaidColumn<BuyerTableRow>(t, true),
        latestPaymentStatusColumn,
        createdAtColumn,
        actionsColumn
      ];
    }

    return [
      fullNameColumn,
      createAssetColumn<BuyerTableRow>(t, true),
      createPaymentStatusColumn<BuyerTableRow>(t, true),
      ...(strategy.showsCollectionCapHint
        ? [createPurchasePriceColumn<BuyerTableRow>(t, true)]
        : []),
      createAmountToPayColumn<BuyerTableRow>(strategy, t, true),
      createDepositPaidColumn<BuyerTableRow>(t, true),
      latestPaymentStatusColumn,
      createdAtColumn,
      ...(strategy.showsRegulatoryReports
        ? [createRegulatoryReportsColumn<BuyerTableRow>(t, trustAccountId)]
        : []),
      actionsColumn
    ];
  }, [
    onRequestPayment,
    onViewBuyer,
    onEditBuyer,
    onDeleteBuyer,
    canUpdateBuyer,
    canDeleteBuyer,
    canCollectPayments,
    t,
    trustAccountId,
    strategy,
    showsCoBuyersInOneRow
  ]);

  return (
    <Table
      columns={columns}
      data={rows}
      sorting={sorting}
      onSortingChange={onSortingChange}
      onRowClick={row => onViewBuyer(row.buyers[0], row.purchase)}
      manualSorting
      emptyStateText={t('common:noResults')}
    />
  );
};
